/**
 * `<friends-pane>`: friends, the requests waiting on them, and a way to add
 * one. Phase 1 has no server behind this, so requests stay on this device -
 * and the pane says so in plain words rather than pretending otherwise.
 *
 * Every request it raises is the app's to answer, so they are composed and
 * travel out through whatever page the pane sits on.
 */

import type { FriendStatus } from "../../server/protocol.js";
import type { StoredFriend } from "../db.js";
import { h, keepFocus, toNodes } from "../dom.js";
import { emit } from "../events.js";
import { BaseElement } from "./base-element.js";
import styles from "./friends-pane.css";

const STATUS_WORDS: Readonly<Record<FriendStatus, string>> = {
    "in-game": "At a table",
    "offline": "Offline",
    "online": "Online"
};

export class FriendsPane extends BaseElement {
    #friends: readonly StoredFriend[] = [];

    readonly #content = h("div", { class: "content" });

    readonly #input = h("input", {
        id: "friend-name",
        type: "text",
        placeholder: "PurplePanda#1234",
        autocomplete: "off"
    });

    readonly #send = h("button", { type: "submit", disabled: true }, "Send request");

    constructor() {
        super(styles);

        const form = h(
            "form",
            {},
            h("label", { class: "label", for: "friend-name" }, "Add a friend"),
            h("div", { class: "row" }, this.#input, this.#send),
            h(
                "p",
                { class: "help" },
                "Requests wait for the other player to accept. Until the saloon has a server to carry them, they stay on this device."
            )
        );

        this.#input.addEventListener("input", () => {
            this.#send.disabled = this.#input.value.trim() === "";
        });

        form.addEventListener("submit", (event) => {
            event.preventDefault();

            const name = this.#input.value.trim();

            if (name !== "") {
                emit(this, "friend-add", { name }, true);
                this.#input.value = "";
                this.#send.disabled = true;
            }
        });

        this.root.append(h("h2", { id: "friends-heading" }, "Friends"), this.#content, form);
    }

    set friends(value: readonly StoredFriend[]) {
        this.#friends = value;
        this.invalidate();
    }

    protected render(): void {
        this.setAttribute("role", "region");
        this.setAttribute("aria-label", "Friends");

        const incoming = this.#friends.filter((friend) => friend.relationship === "incoming");
        const outgoing = this.#friends.filter((friend) => friend.relationship === "outgoing");
        const accepted = this.#friends.filter((friend) => friend.relationship === "friend");

        keepFocus(this.root, () => {
            this.#content.replaceChildren(...toNodes([
                incoming.length > 0
                    ? h("div", { "class": "requests", "role": "group", "aria-label": "Friend requests" }, ...incoming.map((friend) => this.#renderRequest(friend)))
                    : null,
                accepted.length === 0 && outgoing.length === 0
                    ? h("p", { class: "empty" }, "Nobody yet. Add someone by their display name.")
                    : h(
                        "ul",
                        { class: "list" },
                        ...accepted.map((friend) => this.#renderFriend(friend, STATUS_WORDS[friend.status], friend.status, "Remove", `Remove ${friend.name}`)),
                        ...outgoing.map((friend) => this.#renderFriend(friend, "Request sent", "offline", "Cancel", `Cancel the request to ${friend.name}`))
                    )
            ]));
        });
    }

    #renderRequest(friend: StoredFriend): HTMLElement {
        const accept = h("button", { "type": "button", "data-focus-key": `accept-${friend.id}` }, "Accept");
        const decline = h("button", { "type": "button", "class": "quiet", "data-focus-key": `decline-${friend.id}` }, "Decline");

        accept.addEventListener("click", () => emit(this, "friend-accept", { id: friend.id }, true));
        decline.addEventListener("click", () => emit(this, "friend-decline", { id: friend.id }, true));

        return h(
            "p",
            { class: "request" },
            h("span", {}, h("strong", {}, friend.name), " wants to be friends."),
            h("span", { class: "row" }, accept, decline)
        );
    }

    #renderFriend(friend: StoredFriend, status: string, dot: FriendStatus, action: string, label: string): HTMLLIElement {
        const button = h(
            "button",
            { "type": "button", "class": "quiet", "aria-label": label, "data-focus-key": `remove-${friend.id}` },
            action
        );

        button.addEventListener("click", () => emit(this, "friend-remove", { id: friend.id }, true));

        return h(
            "li",
            {},
            h("span", { "class": "dot", "data-status": dot, "aria-hidden": "true" }),
            h("span", { class: "name" }, friend.name),
            h("span", { class: "status" }, status),
            button
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "friends-pane": FriendsPane;
    }
}

customElements.define("friends-pane", FriendsPane);
